import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import * as Sentry from '@sentry/react'
import { addDays, format } from 'date-fns'
import { DSButton, DSSpacer, DSText } from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { Quizzes } from '../../redux/quiz/types'
import { getIdealWeightFromQuiz, getWeightFromQuiz } from '../../libs/weight/weightCalculations'
import { Weight, WeightPlanWithDates, WeightUnit } from '../../libs/weight/types'
import { ProjectionGraph, WEIGHT_LINE } from './ProjectionGraph'

export type WeightCalculationScreenProps = {
  title: string
  description?: string
  // When true, widens the chart so its plot area lines up with the page margins
  // (used by e7). Defaults to the original narrower layout for other funnels.
  wideChart?: boolean
  // e10/e11: render the redesigned line graph card instead of the pill chart.
  projectionGraph?: boolean
}

type Props = WeightCalculationScreenProps & {
  onNextClick: onNextClickType
  quiz: Quizzes
}

// ────────────────────────────────────────────────────────────────────────────
// Weight prediction logic (from Figma https://www.figma.com/design/SHNQFk5bsHc6os6mZLhpTu/Web-quiz?node-id=11753-25898&t=cuDvxpU409q86rRt-11)
//
//  • If desired weight loss > 8%: cap at 8% loss over 90 days
//  • If desired weight loss ≤ 8%: show the user's actual goal over 90 days
// ────────────────────────────────────────────────────────────────────────────

const MAX_WEIGHT_LOSS_PERCENT = 0.08
const PLAN_DURATION_DAYS = 90

export const buildPredictedWeightPlan = (
  startWeight: Weight,
  idealWeight: Weight,
  startDate: Date = new Date(),
): { weightPlan: WeightPlanWithDates; endWeight: Weight } => {
  const requestedLoss = startWeight.value - idealWeight.value
  const maxLoss = startWeight.value * MAX_WEIGHT_LOSS_PERCENT
  const cappedLoss = Math.max(0, Math.min(requestedLoss, maxLoss))
  const endValue = startWeight.value - cappedLoss

  return {
    weightPlan: [
      { weight: startWeight.value, date: startDate },
      { weight: endValue, date: addDays(startDate, PLAN_DURATION_DAYS) },
    ],
    endWeight: { value: endValue, unit: startWeight.unit },
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Chart geometry
//
// The chart is rendered as an SVG with a fixed viewBox aspect ratio. The SVG
// element itself fills 100% of its parent's width and the height is computed
// from the viewBox aspect ratio (`height: auto` on the <svg>), so the chart
// is fully responsive — the parent container controls how big it actually
// renders on screen.
//
// All measurements below are expressed as RATIOS of the viewBox width and
// height (typically 0..1). They are converted to viewBox units (`VIEW_W` /
// `VIEW_H`) only at the point of use. To tweak the layout, change a ratio.
//
// `VIEW_W` and `VIEW_H` themselves are arbitrary — they're just the unit-of-
// measurement for the coordinate system, not pixels. They're set to 1000x700
// (close to the original 420x300 aspect ratio of 1.4) so that derived values
// like font sizes are integer-friendly.
//
// All ratio values below (padding, stroke widths, pill sizes, etc.) are
// eyeballed to match the Figma design — they are not derived from a formula.
// ────────────────────────────────────────────────────────────────────────────

const VIEW_W = 1000
const VIEW_H = 700

// Padding around the plot area, expressed as ratios of the viewBox dimensions.
const PADDING = {
  top: 64 / 300,
  bottom: 48 / 300,
  left: 36 / 420,
  right: 36 / 420,
}

const PLOT_LEFT = VIEW_W * PADDING.left
const PLOT_RIGHT = VIEW_W * (1 - PADDING.right)
const PLOT_TOP = VIEW_H * PADDING.top
const PLOT_BOTTOM = VIEW_H * (1 - PADDING.bottom)
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP
const BASELINE_Y = PLOT_BOTTOM

// The first/last data points are pinned to specific gridlines so the curve
// always has the same visual shape, regardless of the actual weight values.
// Fractions are measured from the BOTTOM of the plot area (0 = baseline, 1 = top).
const GRIDLINE_FRACTIONS = [0, 0.25, 0.5, 0.75, 1] as const
const START_POINT_GRID_FRACTION = 1 // top gridline
const END_POINT_GRID_FRACTION = 0.25 // one gridline above the baseline

const gridY = (fraction: number) => PLOT_TOP + PLOT_HEIGHT * (1 - fraction)
const START_POINT_Y = gridY(START_POINT_GRID_FRACTION)
const END_POINT_Y = gridY(END_POINT_GRID_FRACTION)

// All stroke widths and dot radii expressed as ratios of viewBox HEIGHT.
const STROKE = {
  line: VIEW_H * 0.024,
  guide: VIEW_H * 0.005,
  grid: VIEW_H * 0.0034,
} as const

const DOT_RADIUS = VIEW_H * 0.024

// Pill labels — sizes are ratios of viewBox HEIGHT, gaps are ratios of width.
const PILL = {
  singleLineHeight: VIEW_H * 0.094,
  fontSize: VIEW_H * 0.047,
  lineHeight: VIEW_H * 0.052, // distance between baselines for wrapped pills
  gapAbove: VIEW_H * 0.06,
  gapLeft: VIEW_W * 0.048,
  minWidth: VIEW_W * 0.135,
  maxWidth: VIEW_W * 0.2, // wrap to 2 lines if a single-line label exceeds this
  // [i18n] This fixed character width only works for Latin/ASCII glyphs.
  // For CJK, Arabic, or other wide/complex scripts, replace with a proper
  // text measurement approach (e.g. canvas measureText or a lookup table).
  charWidth: VIEW_W * 0.018,
  horizontalPadding: VIEW_W * 0.048,
} as const

// Date label sits this far below the baseline, expressed as ratio of viewBox height.
const DATE_LABEL_OFFSET_Y = VIEW_H * 0.073
const DATE_LABEL_FONT_SIZE = VIEW_H * 0.04

const COLOR = {
  yellow: 'var(--colour-surface-yellow-bold, #f4c430)',
  grid: 'var(--colour-border-default, #e5e0d8)',
  textPrimary: 'var(--colour-text-primary, #1a1a1a)',
  textSecondary: 'var(--colour-text-secondary, #6b6b6b)',
} as const

// ────────────────────────────────────────────────────────────────────────────
// Animation timing
// ────────────────────────────────────────────────────────────────────────────

const PATH_DELAY_MS = 800
const PATH_DRAW_MS = 1600
const END_ELEMENTS_DELAY_MS = PATH_DELAY_MS + PATH_DRAW_MS

const PATH_DRAW_EASE = 'cubic-bezier(0.2, 0.55, 0.35, 0.95)'

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

export const formatWeight = (value: number, unit: WeightUnit): string => {
  const rounded = Math.round(value)
  if (unit === 'stone') {
    const stones = Math.floor(rounded / 14)
    const lbs = rounded % 14
    return lbs === 0 ? `${stones}st` : `${stones}st ${lbs}lbs`
  }
  return unit === 'lbs' ? `${rounded}lbs` : `${rounded}kg`
}

// Approximate width of a single line of pill text in viewBox units.
const measurePillLine = (text: string): number => text.length * PILL.charWidth + PILL.horizontalPadding

// If a label is too wide for the pill's max width, split it into two lines on
// a space (e.g. "10st 2lbs" → ["10st", "2lbs"]). Single-token labels are left
// as-is — there's nothing sensible to break on.
const splitLabelToLines = (label: string): string[] => {
  if (measurePillLine(label) <= PILL.maxWidth) return [label]
  const spaceIdx = label.indexOf(' ')
  if (spaceIdx <= 0) return [label]
  return [label.slice(0, spaceIdx), label.slice(spaceIdx + 1)]
}

// Build a single smooth "exponential decay" curve from start to end —
// steep initial drop, then flattens. Matches the Figma curve shape.
// Control point offsets (0.18, 0.45) are eyeballed to match the Figma design.
const buildCurvePath = (start: Point, end: Point): string => {
  const dx = end.x - start.x
  // First control point pulls the line straight down from the start.
  // Second control point keeps it flat as it approaches the end.
  const cp1 = { x: start.x + dx * 0.18, y: end.y }
  const cp2 = { x: start.x + dx * 0.45, y: end.y }
  return `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`
}

type Point = { x: number; y: number }
type ChartPoint = Point & { weight: number; date: Date }

// ────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────────

type PillPlacement = 'above' | 'left'

type PillProps = {
  point: Point
  label: string
  placement: PillPlacement
}

const PillLabel: React.FC<PillProps> = ({ point, label, placement }) => {
  const lines = splitLabelToLines(label)
  const lineWidths = lines.map(measurePillLine)
  const width = Math.max(PILL.minWidth, ...lineWidths)
  const height =
    lines.length === 1 ? PILL.singleLineHeight : PILL.singleLineHeight + PILL.lineHeight * (lines.length - 1)
  const cornerRadius = height / 2

  // Anchor the pill relative to its data point.
  let rectX = placement === 'above' ? point.x - width / 2 : point.x - width - PILL.gapLeft
  const rectY = placement === 'above' ? point.y - height - PILL.gapAbove : point.y - height / 2

  // Clamp horizontally so the pill never extends outside the viewBox, even
  // when the label is unusually long.
  rectX = Math.max(0, Math.min(rectX, VIEW_W - width))

  // Vertically centre the block of text in the pill. Empirically, dominantBaseline
  // "middle" centres a single tspan well; for multi-line we offset by half the
  // total text block height around the pill centre.
  const centerY = rectY + height / 2
  const totalTextHeight = (lines.length - 1) * PILL.lineHeight
  const firstLineY = centerY - totalTextHeight / 2

  return (
    <>
      <rect x={rectX} y={rectY} width={width} height={height} rx={cornerRadius} fill={COLOR.yellow} />
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        fill={COLOR.textPrimary}
        fontSize={PILL.fontSize}
        fontWeight="600"
      >
        {lines.map((line, i) => (
          <tspan key={i} x={rectX + width / 2} y={firstLineY + i * PILL.lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </>
  )
}

const DataPointDot: React.FC<{ point: Point }> = ({ point }) => (
  <circle cx={point.x} cy={point.y} r={DOT_RADIUS} fill={COLOR.yellow} />
)

const VerticalGuide: React.FC<{ point: Point }> = ({ point }) => (
  <line
    x1={point.x}
    y1={point.y}
    x2={point.x}
    y2={BASELINE_Y}
    stroke={COLOR.yellow}
    strokeWidth={STROKE.guide}
    strokeDasharray="3 4"
  />
)

const DateLabel: React.FC<{ point: Point; date: Date }> = ({ point, date }) => (
  <text
    x={point.x}
    y={BASELINE_Y + DATE_LABEL_OFFSET_Y}
    textAnchor="middle"
    fill={COLOR.textSecondary}
    fontSize={DATE_LABEL_FONT_SIZE}
  >
    {format(date, 'MMM d')}
  </text>
)

// ────────────────────────────────────────────────────────────────────────────
// Chart
// ────────────────────────────────────────────────────────────────────────────

const ChartSurface = styled.div<{ $wide?: boolean }>`
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-12);
  background-color: #ffffff;
  padding: ${({ $wide }) =>
    $wide ? 'var(--grid-16) 0 var(--grid-8) 0' : 'var(--grid-16) var(--grid-8) var(--grid-8) var(--grid-8)'};

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  @keyframes weight-chart-end-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes weight-chart-path-draw {
    from {
      stroke-dashoffset: 1;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
`

const WeightPlanChart: React.FC<{ weightPlan: WeightPlanWithDates; unit: WeightUnit; wide?: boolean }> = ({
  weightPlan,
  unit,
  wide,
}) => {
  const [pathVisible, setPathVisible] = useState(false)
  const [endVisible, setEndVisible] = useState(false)

  // When wide, pull the left/right plot edges out toward the SVG edges so the
  // chart lines up with the page margins. Default keeps the original inset.
  const plotLeft = wide ? VIEW_W * (12 / 420) : PLOT_LEFT
  const plotRight = wide ? VIEW_W * (1 - 12 / 420) : PLOT_RIGHT

  useEffect(() => {
    const pathTimer = window.setTimeout(() => setPathVisible(true), PATH_DELAY_MS)
    const endTimer = window.setTimeout(() => setEndVisible(true), END_ELEMENTS_DELAY_MS)
    return () => {
      window.clearTimeout(pathTimer)
      window.clearTimeout(endTimer)
    }
  }, [])

  // We always render exactly two points (start and end), pinned to fixed
  // gridlines so the curve has a consistent visual shape.
  const points: [ChartPoint, ChartPoint] | null = useMemo(() => {
    if (weightPlan.length < 2) return null
    const first = weightPlan[0]
    const last = weightPlan[weightPlan.length - 1]
    return [
      { x: plotLeft, y: START_POINT_Y, weight: first.weight, date: first.date },
      { x: plotRight, y: END_POINT_Y, weight: last.weight, date: last.date },
    ]
  }, [weightPlan, plotLeft, plotRight])

  if (!points) return null

  const [startPoint, endPoint] = points
  const curvePath = buildCurvePath(startPoint, endPoint)

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet">
      {/* Horizontal gridlines */}
      {GRIDLINE_FRACTIONS.map((fraction) => {
        const y = gridY(fraction)
        return (
          <line
            key={fraction}
            x1={plotLeft}
            y1={y}
            x2={plotRight}
            y2={y}
            stroke={COLOR.grid}
            strokeWidth={STROKE.grid}
          />
        )
      })}

      {/* Start state — visible immediately */}
      <VerticalGuide point={startPoint} />
      <DataPointDot point={startPoint} />
      <PillLabel point={startPoint} label={formatWeight(startPoint.weight, unit)} placement="above" />
      <DateLabel point={startPoint} date={startPoint.date} />

      {/* Animated curve — only mounted into the DOM after the delay so there
          are no rendering artifacts (e.g. round line caps) before it draws. */}
      {pathVisible && (
        <path
          d={curvePath}
          pathLength={1}
          fill="none"
          stroke={COLOR.yellow}
          strokeWidth={STROKE.line}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: `weight-chart-path-draw ${PATH_DRAW_MS}ms ${PATH_DRAW_EASE} forwards`,
          }}
        />
      )}

      {/* End state — only mounted after the curve finishes drawing */}
      {endVisible && (
        <g style={{ animation: 'weight-chart-end-fade 0.3s ease-out forwards', opacity: 0 }}>
          <VerticalGuide point={endPoint} />
          <DataPointDot point={endPoint} />
          <PillLabel point={endPoint} label={formatWeight(endPoint.weight, unit)} placement="above" />
          <DateLabel point={endPoint} date={endPoint.date} />
        </g>
      )}
    </svg>
  )
}

// e10/e11 "Weight loss" line graph. Stone weights are stored in lbs, so the axis is
// plotted in stones (half-stone steps) while the bubble keeps the st/lbs format.
const WeightProjectionGraph: React.FC<{ weightPlan: WeightPlanWithDates; unit: WeightUnit }> = ({
  weightPlan,
  unit,
}) => {
  const start = weightPlan[0]
  const end = weightPlan[weightPlan.length - 1]
  const toAxis = (lbsOrKg: number) => (unit === 'stone' ? lbsOrKg / 14 : lbsOrKg)
  return (
    // No y-axis: the line always runs the full height of the card, so a small
    // goal (or one equal to the current weight) still slopes down, as the
    // previous chart did.
    <ProjectionGraph
      title="Weight loss"
      startValue={toAxis(start.weight)}
      endValue={toAxis(end.weight)}
      bubbleText={formatWeight(end.weight, unit)}
      endDateLabel={format(end.date, 'd MMM')}
      line={WEIGHT_LINE}
      dotColor="#FFD100"
      bubbleColor="#FFD100"
      bubblePaddingX={8}
      cardPaddingY={16}
    />
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Screen
// ────────────────────────────────────────────────────────────────────────────

export const WeightCalculationScreen: React.FC<Props> = ({
  title,
  description,
  wideChart,
  projectionGraph,
  onNextClick,
  quiz,
}) => {
  const startWeight = useMemo(() => getWeightFromQuiz(quiz), [quiz])
  const idealWeight = useMemo(() => getIdealWeightFromQuiz(quiz), [quiz])

  useEffect(() => {
    if (!startWeight || !idealWeight) {
      Sentry.captureException(
        new Error(
          `WeightCalculationScreen: missing weight data — startWeight: ${JSON.stringify(
            startWeight,
          )}, idealWeight: ${JSON.stringify(idealWeight)}`,
        ),
      )
    }
  }, [startWeight, idealWeight])

  const weightPlan = useMemo(() => {
    if (!startWeight || !idealWeight) return [] as WeightPlanWithDates
    return buildPredictedWeightPlan(startWeight, idealWeight).weightPlan
  }, [startWeight, idealWeight])

  const weightUnit: WeightUnit = startWeight?.unit ?? 'lbs'

  return (
    <ScreenContainer>
      <DSText variant="fluid-heading-700" weight="semiBold" align="left">
        {title}
      </DSText>
      <DSSpacer size={24} direction="vertical" />

      {weightPlan.length > 0 && projectionGraph && <WeightProjectionGraph weightPlan={weightPlan} unit={weightUnit} />}

      {weightPlan.length > 0 && !projectionGraph && (
        <ChartSurface $wide={wideChart}>
          <WeightPlanChart weightPlan={weightPlan} unit={weightUnit} wide={wideChart} />
        </ChartSurface>
      )}

      {description && (
        <>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-paragraph-300" color="secondary">
            <ContextAwareText text={description} quiz={quiz} />
          </DSText>
        </>
      )}

      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={onNextClick}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              Continue
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </ScreenContainer>
  )
}
